import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { FeatureFlags } from '@ace-tracker/shared';
import type { ViewMode } from '../urlState';
import { Sidebar } from './Sidebar';

const COLLAPSED_KEY = 'ace-dashboard-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Page frame: a left sidebar (≥1024px, collapsible to an icon rail) or an
 * overlay drawer (<1024px, opened from the filter bar's menu button), and a
 * main column holding the sticky filter bar and the page content.
 *
 * `fitViewport` (Executive view): on screens matching the `fit` breakpoint
 * (tailwind.config.js) the main column is exactly one viewport tall and the
 * content fills the space under the header instead of scrolling. Elsewhere
 * the page scrolls as usual.
 */
export function AppShell({
  view,
  flags,
  onViewChange,
  username,
  onLogout,
  filterBar,
  fitViewport = false,
  children,
}: {
  view: ViewMode;
  flags: FeatureFlags;
  onViewChange: (view: ViewMode) => void;
  username: string | null;
  onLogout: () => void;
  /** Rendered sticky at the top of the main column; receives the drawer opener for small screens. */
  filterBar: (openMenu: { onOpen: () => void; buttonRef: React.RefObject<HTMLButtonElement> }) => ReactNode;
  fitViewport?: boolean;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // Storage unavailable (private mode etc.) — the rail just won't be remembered.
    }
  }

  function closeDrawer() {
    setDrawerOpen(false);
    menuButtonRef.current?.focus();
  }

  // Drawer: move focus in on open, close on Escape, and keep Tab inside it.
  useEffect(() => {
    if (!drawerOpen) return;
    const drawer = drawerRef.current;
    const focusables = () =>
      Array.from(drawer?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? []);
    focusables()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
      } else if (e.key === 'Tab') {
        const items = focusables();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  return (
    <div className="flex min-h-screen">
      <aside
        className={`sticky top-0 hidden h-screen shrink-0 border-r border-border bg-surface transition-[width] duration-150 ease-in-out lg:block ${
          collapsed ? 'w-16' : 'w-56'
        }`}
      >
        <Sidebar
          view={view}
          onViewChange={onViewChange}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          username={username}
          onLogout={onLogout}
          flags={flags}
        />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-30 lg:hidden">
          <div className="absolute inset-0 bg-navy/60" aria-hidden="true" onClick={closeDrawer} />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="absolute inset-y-0 left-0 w-64 max-w-[calc(85vw/var(--app-zoom))] border-r border-border bg-surface shadow-lg"
          >
            <Sidebar
              view={view}
              onViewChange={(v) => {
                onViewChange(v);
                closeDrawer();
              }}
              collapsed={false}
              username={username}
              onLogout={onLogout}
              flags={flags}
            />
          </div>
        </div>
      )}

      <div className={`min-w-0 flex-1 ${fitViewport ? 'fit:flex fit:min-h-screen fit:flex-col' : ''}`}>
        {filterBar({ onOpen: () => setDrawerOpen(true), buttonRef: menuButtonRef })}
        {fitViewport ? (
          <main className="mx-auto flex w-full max-w-[1400px] flex-col p-4 sm:px-8 sm:py-5 fit:flex-1">{children}</main>
        ) : (
          <main className="mx-auto max-w-[1400px] space-y-10 p-4 sm:p-8">{children}</main>
        )}
      </div>
    </div>
  );
}
